import { Document, pdf } from '@react-pdf/renderer';
import fileDownloader from 'js-file-download';
import { useTranslation } from 'react-i18next';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { resolveTranslation } from 'utils/general';
import {
  loaders as benefitsLoaders,
  converters as benefitsConverters,
} from 'store/benefits';
import { APP } from 'config';
import { makeRequest } from '../../../../api/api';
import { Endpoints } from '../../../../api/constants';
import { BenefitMarketplaceItemsResponse } from '../../../../types/MarketplaceItems';
import { useLoading } from '../../../../utils/Loading';
import SummaryPage from '../pages/summary/SummaryPage';
import BenefitPDF from '../pages/benefit/BenefitPDF';

export const useBenefitsPDFDownload = () => {
  const { t } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();
  const { me } = useMe();

  const currency = me?.parentCompany?.defaultCurrency ?? APP.currencies.default;

  const generationState = useLoading();

  const fetchMarketplaceData = async (benefitId: string, language: string) => {
    const assignedItemsResponse =
      await makeRequest<BenefitMarketplaceItemsResponse>(
        'get',
        Endpoints.clientAdmin.benefitPlans.byId(benefitId).marketplaceItems
          .root,
      );
    return {
      assignedItemsCount: assignedItemsResponse.data.count,
      firstItems: assignedItemsResponse.data.data
        .slice(0, 3)
        .map(
          (item) =>
            resolveTranslation(item.translations, language, defaultLanguage)
              ?.title ?? '',
        ),
    };
  };

  const fetchBenefitData = async (benefitId: string, language: string) => {
    const benefit = await benefitsLoaders
      .getBenefit({ benefitId })
      .then(benefitsConverters.getBenefit);
    const benefitContent = await benefitsLoaders
      .getBenefitContent({ benefitId })
      .then(benefitsConverters.getBenefitContent);
    const marketplaceData = await fetchMarketplaceData(benefitId, language);
    return {
      benefitData: {
        ...benefit,
        ...benefitContent,
      },
      marketplaceData,
    };
  };

  const downloadPdf = async (
    benefitIds: string[],
    language: string,
    fileName?: string,
  ) => {
    generationState.startLoading();
    try {
      const selectedBenefitsData = await Promise.all(
        benefitIds.map((id) => fetchBenefitData(id, language)),
      );

      const pageReferences = new Map<string, number>();
      const pageRenderHandler = (benefitId: string) => (pageNumber: number) => {
        if (!pageReferences.has(benefitId)) {
          pageReferences.set(benefitId, pageNumber);
        }
      };

      const renderPdf = () => {
        const commonProps = {
          language,
          currency,
        };

        return pdf(
          <Document>
            {selectedBenefitsData.length > 1 ? (
              <SummaryPage
                benefits={selectedBenefitsData.map((data) => data.benefitData)}
                pageReferences={pageReferences}
                {...commonProps}
              />
            ) : null}
            {selectedBenefitsData.map(({ benefitData, marketplaceData }) => (
              <BenefitPDF
                key={benefitData.id}
                data={benefitData}
                marketplaceData={marketplaceData}
                onRenderPage={pageRenderHandler(benefitData.id)}
                {...commonProps}
              />
            ))}
          </Document>,
        ).toBlob();
      };

      // Initial render to gather page references for summary page
      await renderPdf();
      // Final render with all data in place
      const blob = await renderPdf();

      fileDownloader(
        blob,
        `${fileName ?? t('benefits.selectedBenefitsPDFFileName')}.pdf`,
      );
    } finally {
      generationState.stopLoading();
    }
  };

  return {
    downloadPdf,
    loading: generationState.loading,
  };
};
